import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@wlb/shared/context/AuthContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { validateUsernameOrEmail } from '@wlb/shared/utils/validation';
import { clearStaleUserSessionIfUnauthenticated } from '@wlb/shared/api/auth/clearUserSession';
import { BrandLogo } from '../components/ui/BrandLogo';
import { FormError } from '../components/ui/FormError';
import { PasswordInput } from '../components/ui/PasswordInput';
import { AuthFlowLayout } from '../components/layout/AuthFlowLayout';
import {
  clearRememberedEmail,
  getRememberedEmail,
  setRememberedEmail,
} from '../features/auth/rememberMe';
import type { AuthFlowState } from '../features/auth/authFlowState';
import {
  AVAILABLE_SOCIAL_PROVIDERS,
  socialProviderLabelKey,
} from '../features/auth/socialProviders';
import { useSocialAuth } from '../features/auth/useSocialAuth';
import { GoogleSignInButton } from '../features/auth/GoogleSignInButton';
import { SocialProviderIcon } from '../features/auth/SocialProviderIcon';
import '../features/auth/auth.css';

export function Login() {
  const { loginWithEmail, isAuthenticated, loading, error, clearError } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as AuthFlowState | null)?.from ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const attemptedRef = useRef(false);

  // The isAuthenticated effect below handles navigation for SSO too.
  const social = useSocialAuth({ mode: 'login' });

  useEffect(() => {
    clearStaleUserSessionIfUnauthenticated().catch(() => undefined);
  }, []);

  useEffect(() => {
    clearError();
    let active = true;
    getRememberedEmail().catch(() => '').then(remembered => {
      if (!active || !remembered) return;
      setEmail(remembered);
      // A stored email only exists when the box was ticked; leaving it
      // unchecked would clear the preference on the next sign-in.
      setRememberMe(true);
    });
    return () => {
      active = false;
    };
  }, [clearError]);

  // Navigate only when the session flips to authenticated — never inside the
  // submit handler. loginWithEmail writes state.error instead of throwing.
  useEffect(() => {
    if (isAuthenticated) navigate(from, { replace: true });
  }, [isAuthenticated, from, navigate]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    clearError();

    const idResult = validateUsernameOrEmail(email);
    const nextEmailError = idResult.valid ? '' : t(idResult.message);
    const nextPasswordError = password ? '' : t('validation.passwordRequired');
    setEmailError(nextEmailError);
    setPasswordError(nextPasswordError);
    if (!idResult.valid || !password) return;

    await (rememberMe ? setRememberedEmail(email.trim()) : clearRememberedEmail());
    attemptedRef.current = true;
    await loginWithEmail({ email: email.trim(), password });
  };

  const serverError = attemptedRef.current ? error : '';
  const formError = social.error || serverError;
  const busy = loading || social.pending !== null;

  return (
    <AuthFlowLayout>
      <div className="auth-flow__card">
        <BrandLogo compact className="auth-flow__brand" />
        <h1 className="auth-flow__heading">{t('auth.login.heading')}</h1>
        <p className="auth-flow__subheading">{t('auth.login.subheading')}</p>
        <form className="auth-flow__form" onSubmit={handleSubmit} noValidate>
          <FormError variant="banner" message={formError} id="login-form-error" />
          <label className="auth-flow__field">
            <span>{t('auth.login.usernameOrEmailLabel')}</span>
            <input
              className={`auth-flow__input${emailError ? ' auth-flow__input--error' : ''}`}
              type="text"
              value={email}
              onChange={event => {
                setEmail(event.target.value);
                if (emailError) setEmailError('');
              }}
              placeholder={t('auth.login.usernameOrEmailPlaceholder')}
              autoComplete="username"
              aria-invalid={Boolean(emailError)}
              aria-describedby={emailError ? 'login-email-error' : undefined}
              autoFocus
            />
            <FormError message={emailError} id="login-email-error" />
          </label>
          <label className="auth-flow__field">
            <span>{t('auth.loginPassword.passwordLabel')}</span>
            <PasswordInput
              className={`auth-flow__input${passwordError ? ' auth-flow__input--error' : ''}`}
              value={password}
              onChange={event => {
                setPassword(event.target.value);
                if (passwordError) setPasswordError('');
              }}
              placeholder={t('auth.loginPassword.passwordPlaceholder')}
              autoComplete="current-password"
              aria-invalid={Boolean(passwordError)}
              aria-describedby={passwordError ? 'login-password-error' : undefined}
            />
            <FormError message={passwordError} id="login-password-error" />
          </label>

          <div className="auth-flow__remember">
            <label className="auth-flow__checkbox">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={event => setRememberMe(event.target.checked)}
              />
              {t('auth.loginPassword.rememberMe')}
            </label>
            <Link className="auth-flow__link-button" to="/forgot-password">
              {t('auth.loginPassword.forgotPassword')}
            </Link>
          </div>

          <button className="auth-flow__primary" type="submit" disabled={busy}>
            {busy ? t('common.loading') : t('common.buttons.signIn')}
          </button>

          {AVAILABLE_SOCIAL_PROVIDERS.length > 0 && (
            <>
              <div className="auth-flow__divider">{t('common.orContinueWith')}</div>
              {AVAILABLE_SOCIAL_PROVIDERS.map(provider =>
                provider === 'google' ? (
                  <GoogleSignInButton
                    key={provider}
                    disabled={busy}
                    onResult={result => social.complete('google', result)}
                  />
                ) : (
                  <button
                    key={provider}
                    className="auth-flow__social"
                    type="button"
                    onClick={() => social.start(provider)}
                    disabled={busy}
                  >
                    <SocialProviderIcon provider={provider} />
                    {social.pending === provider
                      ? t('common.loading')
                      : t(socialProviderLabelKey(provider))}
                  </button>
                ),
              )}
            </>
          )}
        </form>
        <p className="auth-flow__footer">
          {t('auth.common.dontHaveAccount')}
          <Link to="/signup">{t('common.buttons.signUp')}</Link>
        </p>
      </div>
    </AuthFlowLayout>
  );
}
